import React from "react";
import * as Sentry from "@sentry/react";
import { twMerge } from "tailwind-merge";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { IconButton } from "@components/IconButton";
import { ArrowUpRight, Trash01 } from "untitledui-js/react";
import { SearchListProps } from "../../../types/SearchTypes";

// Skeleton loading component
const SearchListSkeleton: React.FC<{ id: string }> = ({ id }) => {
  return (
    <div data-testid={`search-list-skeleton-${id}`}>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={`skeleton-${index}`} className="mb-4 w-full animate-pulse">
          <div className="mb-2 h-4 w-full rounded bg-tertiary-300 lg:w-1/2 dark:bg-tertiary-700"></div>
          <div className="h-3 w-1/2 rounded bg-tertiary-400 lg:w-1/3 dark:bg-tertiary-700"></div>
        </div>
      ))}
    </div>
  );
};

export const SearchList: React.FC<SearchListProps> = ({
  id,
  items,
  onNavigateAction,
  onDeleteAction,
  isLoading,
  showControls = false,
  viewLabel = "View",
  deleteLabel = "Delete",
  ...rest
}) => {
  if (isLoading) {
    return <SearchListSkeleton id={id} />;
  }

  if (!items || items.length === 0) {
    return null;
  }

  // Enhanced - More context and user feedback consideration
  const handleNavigateAction = (itemId: string) => {
    try {
      if (!onNavigateAction) {
        throw new Error("No onNavigateAction provided");
      }
      // Log the action for debugging
      onNavigateAction(itemId);
    } catch (error) {
      console.error("SearchList action failed:", { itemId, error });
      Sentry.captureException(error);
    }
  };

  const handleKeyDown = (itemId: string) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleNavigateAction(itemId);
    }
  };

  return (
    <div
      data-testid={`${id}-list`}
      className="mb-4 flex flex-col gap-4 lg:mb-8"
      {...rest}
    >
      {items.map((session) => {
        const { name, description, id } = session;
        return (
          <div
            className={twMerge(
              "group",
              "flex justify-between gap-2",
              "transition-border-color duration-300",
              "hover:border-primary-500",
              "group:focus-within:border-primary-500"
            )}
            key={id}
          >
            <div className="flex w-full">
              <button
                onClick={() => handleNavigateAction(id)}
                onKeyDown={handleKeyDown(id)}
                data-testid={`search-item-${id}`}
                className="w-full text-left transition-colors focus:outline-none dark:focus:bg-tertiary-800"
                aria-label={`${name} - ${description}`}
                tabIndex={0}
              >
                <Typography className="overflow-hidden text-lg font-semibold text-tertiary-50">
                  {name}
                </Typography>
                {description && (
                  <Typography className="mb-3 overflow-hidden text-sm text-tertiary-400">
                    {description}
                  </Typography>
                )}
              </button>
            </div>
            {showControls && (
              <div className="flex flex-col items-end justify-between gap-4">
                <div className="flex items-center gap-2">
                  <IconButton
                    id={`delete-${id}`}
                    className="group-hover:bg-white/10 hover:bg-primary-400 hover:text-secondary-950"
                    onClick={() => {
                      if (!onDeleteAction) {
                        console.error("No onDeleteAction provided");
                        return;
                      }
                      onDeleteAction(id);
                    }}
                    ariaLabel={deleteLabel}
                    icon={<Trash01 className="h-4 w-4" />}
                    variant="sm"
                  />
                  <IconButton
                    id={`navIndicator-${id}`}
                    onClick={() => {
                      // navigate action
                      if (!onNavigateAction) {
                        console.error("No onNavigateAction provided");
                        return;
                      }
                      handleNavigateAction(id);
                    }}
                    className="group-hover:bg-primary-400 hover:bg-primary-400"
                    icon={<ArrowUpRight className="h-4 w-4" />}
                    shouldRotate={true}
                    ariaLabel={viewLabel}
                    variant="sm"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
